import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, withKeys as _withKeys, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "flex items-center w-full px-2 py-1 gap-2" }
const _hoisted_2 = { class: "message-input-box" }
const _hoisted_3 = { class: "icon-actions" }

import { ref, computed, onBeforeUnmount } from 'vue';
import Textarea from 'primevue/textarea';
import { EMIT } from '@/enum';
import AddFileModal from '@/components/MessageCenter/components/Chat/components/AddFileModal.vue';
import AudioMessage from '@/components/MessageCenter/components/Chat/components/AudioMessage.vue';
import AudioVisualizer from '@/components/MessageCenter/components/Chat/components/AudioVisualizer.vue';
import { pickBestMime, makeFileName } from '@/composables';
import { useMessageCenterStore } from '@/store';

const BIT_RATES = 128000;


export default /*@__PURE__*/_defineComponent({
  __name: 'InputMessage',
  emits: [EMIT.MESSAGE_SENT],
  setup(__props, { emit: __emit }) {

const emit = __emit;
const messageCenterStore = useMessageCenterStore();
const addFileModalVisible = ref(false);
const isRecording = ref<boolean>(false);
const duration = ref<number>(0);
let mediaRecorder: MediaRecorder | null = null;
const mediaStream = ref<MediaStream | null>(null);
let chunks: Blob[] = [];

const recordedFile = ref<File | null>(null);
const audioUrl = ref<string | null>(null);

const toggleAddFileModal = () => (addFileModalVisible.value = !addFileModalVisible.value);
const hasAudioPreview = computed(() => !!recordedFile.value);

const buildObjectUrl = (file: File | null) => {
  if (audioUrl.value) URL.revokeObjectURL(audioUrl.value);
  audioUrl.value = file ? URL.createObjectURL(file) : null;
};

const startRecording = async () => {
  messageCenterStore.messageText = '';
  if (hasAudioPreview.value) discardAudio();

  try {
    mediaStream.value = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    messageCenterStore?.doToast?.('Cannot access microphone.', 'Error', true);
    return;
  }

  const { mime } = pickBestMime();
  try {
    mediaRecorder = new MediaRecorder(mediaStream.value, { mimeType: mime, audioBitsPerSecond: BIT_RATES });
  } catch {
    messageCenterStore?.doToast?.('Cannot start recorder.', 'Error', true);
    mediaStream.value?.getTracks().forEach((t) => t.stop());
    mediaStream.value = null;
    return;
  }

  chunks = [];
  mediaRecorder.ondataavailable = (e) => e.data?.size && chunks.push(e.data);

  mediaRecorder.onstop = () => {
    const type = chunks[0]?.type || mime;
    const blob = chunks.length ? new Blob(chunks, { type }) : null;

    if (!blob) {
      messageCenterStore?.doToast?.('No audio data recorded.', 'Error', true);
    } else {
      const file = new File([blob], makeFileName(blob.type), { type: blob.type });
      recordedFile.value = file;
      buildObjectUrl(file);
    }

    mediaStream.value?.getTracks().forEach((t) => t.stop());
    mediaStream.value = null;
    mediaRecorder = null;
    chunks = [];
  };

  mediaRecorder.start();
  isRecording.value = true;
};

const stopRecording = () => {
  try {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') mediaRecorder.stop();
  } catch {
    console.error('Error stopping the media recorder');
  } finally {
    isRecording.value = false;
  }
};

const recordMessage = async () => (isRecording.value ? stopRecording() : await startRecording());
const sendAction = () => (hasAudioPreview.value ? sendAudio() : sendMessage());

const sendMessage = () => {
  const message = (messageCenterStore.messageText || '').trim();
  if (!message) return;
  const fd = new FormData();
  fd.append('content', message);
  fd.append('type', 'text');
  messageCenterStore.messageText = '';
  emit(EMIT.MESSAGE_SENT, fd);
};

const sendMessageFile = (formData: FormData) => {
  emit(EMIT.MESSAGE_SENT, formData);
  addFileModalVisible.value = false;
};

const sendAudio = () => {
  if (!recordedFile.value) return;
  const formData = new FormData();
  formData.append('content', messageCenterStore.messageText || '');
  formData.append('type', 'file');
  formData.append('attachment[]', recordedFile.value, recordedFile.value.name);

  const duration = Math.floor((recordedFile.value.size * 8) / BIT_RATES);
  const metadata = {
    mime_type: recordedFile.value.type,
    file_size: recordedFile.value.size,
    duration: Number.isFinite(duration) ? duration : null,
  };
  formData.append('metadata', JSON.stringify(metadata));
  messageCenterStore.messageText = '';
  discardAudio();
  emit(EMIT.MESSAGE_SENT, formData);
};

const discardAudio = () => {
  buildObjectUrl(null);
  recordedFile.value = null;
};

const updateDuration = (newDuration: number) => {
  duration.value = newDuration;
};

onBeforeUnmount(() => {
  if (isRecording.value) stopRecording();
  mediaStream.value?.getTracks().forEach((t) => t.stop());
  mediaStream.value = null;
  mediaRecorder = null;
  chunks = [];
  if (audioUrl.value) URL.revokeObjectURL(audioUrl.value);
});

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        (!hasAudioPreview.value)
          ? (_openBlock(), _createBlock(_component_Icon, {
              key: 0,
              name: isRecording.value ? 'stop' : 'mic',
              class: "audio-control-icon control-icon",
              onClick: recordMessage
            }, null, 8, ["name"]))
          : _createCommentVNode("", true),
        (hasAudioPreview.value && audioUrl.value)
          ? (_openBlock(), _createBlock(AudioMessage, {
              key: 1,
              src: audioUrl.value,
              "meta-duration": duration.value,
              onDiscard: discardAudio
            }, null, 8, ["src", "meta-duration"]))
          : _createCommentVNode("", true),
        (isRecording.value)
          ? (_openBlock(), _createBlock(AudioVisualizer, {
              key: 2,
              stream: mediaStream.value,
              class: "flex-1 waveform-canvas",
              onClose: updateDuration
            }, null, 8, ["stream"]))
          : _createCommentVNode("", true),
        (!hasAudioPreview.value && !isRecording.value)
          ? (_openBlock(), _createBlock(_unref(Textarea), {
              key: 3,
              modelValue: _unref(messageCenterStore).messageText,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(messageCenterStore).messageText) = $event)),
              "auto-resize": true,
              type: "text",
              placeholder: "Message",
              class: "flex-1 text-sm",
              onKeyup: _withKeys(sendMessage, ["enter"])
            }, null, 8, ["modelValue"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_3, [
          (!hasAudioPreview.value && !isRecording.value)
            ? (_openBlock(), _createElementBlock("i", {
                key: 0,
                class: "pi pi-plus-circle control-icon",
                onClick: toggleAddFileModal
              }))
            : _createCommentVNode("", true),
          (!isRecording.value)
            ? (_openBlock(), _createBlock(_component_Icon, {
                key: 1,
                name: "send",
                class: "control-icon",
                onClick: sendAction
              }))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    (addFileModalVisible.value)
      ? (_openBlock(), _createBlock(AddFileModal, {
          key: 0,
          onCancel: toggleAddFileModal,
          onSendMessageFile: sendMessageFile
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})